import { monkeyPatch } from "./monkeyPatch";

monkeyPatch(Set.prototype, {
  /**
   * Convenience, Vereinheitlichung mit Array.includes
   * @param {any} element
   * @returns {boolean}
   */
  includes<T>(this: Set<T>, element: T): boolean {
    return this.has(element);
  },

  /**
   * Prüft, ob das Predicate für jedes Element true ergibt
   * @param {function} predicate
   * @returns {boolean}
   */
  every<T>(this: Set<T>, predicate: (value: T) => boolean): boolean {
    let t = true;
    this.forEach((x) => {
      t = t && predicate(x);
    });
    return t;
  },

  /**
   * Prüft, ob das Predicate für wenigstens ein Element true ergibt
   * @param {function} predicate
   * @returns {boolean}
   */
  some<T>(this: Set<T>, predicate: (value: T) => boolean): boolean {
    let t = false;
    this.forEach((x) => {
      t = t || predicate(x);
    });
    return t;
  },

  /**
   * Prüft, ob dieser Set einen anderen enthält
   * @param {Set} other
   * @returns {boolean}
   */
  containsSet<T>(this: Set<T>, other: Set<T>): boolean {
    if (other) {
      return other.every((x) => this.has(x));
    }
    return true;
  },

  /**
   * Prüft, ob zwei Sets gleich sind
   * @param {Set} other
   * @returns {boolean}
   */
  equals<T>(this: Set<T>, other: Set<T>): boolean {
    return !!(other && this.size === other.size && this.containsSet(other));
  },

  /**
   * Gibt einen Set zurück, der nur Elemente enthält, für die Predicate true ist.
   * @param {function} predicate
   * @returns {boolean}
   */
  filter<T>(this: Set<T>, predicate: (value: T) => boolean): Set<T> {
    const result = new Set<T>();
    this.forEach((x) => {
      if (predicate(x)) {
        result.add(x);
      }
    });
    return result;
  },

  /**
   * Gibt die Schnittmenge mit einem anderen Set zurück
   * @param {Set} other
   * @returns {Set}
   */
  intersection<T>(this: Set<T>, other: Set<T> | T[]): Set<T> {
    if (other) {
      // Handle both Set and Array logic if necessary, or assume Set methods exist on 'other'?
      // Original code calls other.has(x). Array doesn't have .has().
      // If other is Array, this fails at runtime in original JS unless other is converted or has .has monkeypatched?
      // Wait, Array DOES NOT have .has().
      // So 'other' MUST be a Set.
      // But I typed it as Set | T[] | undefined in global.d.ts?
      // I should stick to Set<T> if original logic assumes .has().
      // Or checking if other is Set.
      // JS Doc said @param {Set} other.
      // So I assume Set.
      return this.filter((x) => ((other as any).has ? (other as unknown as Set<T>).has(x) : false));
    }
    return new Set<T>();
  },

  /**
   * Gibt die Differenz zu einem anderen Set zurück
   * @param {Set} other
   * @returns {Set}
   */
  difference<T>(this: Set<T>, other: Set<T>): Set<T> {
    if (other) {
      return this.filter((x) => !other.has(x));
    }
    return new Set<T>(this);
  },

  /**
   * Gibt die Vereiningung mit einem anderen Set zurück
   * @param {Set} other
   * @returns {Set}
   */
  union<T>(this: Set<T>, other: Set<T> | T[] | undefined): Set<T> {
    const result = new Set<T>();
    this.forEach((x) => result.add(x));
    if (other) {
      other.forEach((x: T) => result.add(x));
    }
    return result;
  },

  /**
   * fügt alle Elemente einer anderen Collection zum Set hinzu, und verändert diesen Set.
   * @param {Set} other
   */
  addAll<T>(this: Set<T>, other: Set<T> | T[] | undefined): void {
    if (other) {
      other.forEach((x: T) => this.add(x));
    }
  },

  /**
   * Entfernt alle Elemente aus diesem Set einer anderen Collection
   * @param {Set} other
   */
  deleteAll<T>(this: Set<T>, other: Set<T> | T[] | undefined): void {
    if (other) {
      other.forEach((x: T) => this.delete(x));
    }
  },

  /**
   * gibt das erste Element des Sets zurück, oder undefined
   * @returns {any}
   */
  first<T>(this: Set<T>): T | undefined {
    return this.values().next().value;
  },

  /**
   * gibt das erste Element des Sets zurück, für das predicate(Element) true ist, oder null
   * @param {function} predicate
   * @returns {any}
   */
  find<T>(this: Set<T>, predicate: (value: T) => boolean): T | null {
    for (let v of this.values()) {
      if (predicate(v)) {
        return v;
      }
    }
    return null;
  },

  /**
   * wie Array.join
   * @param delimiter
   * @returns {string}
   */
  join<T>(this: Set<T>, delimiter?: string): string {
    return Array.from(this).join(delimiter);
  },

  /**
   * ruft mapper(value) für jeden Eintrag auf und baut ein Set mit den Rückgabewerten auf
   * @param {function} mapper
   * @returns {Set}
   */
  map<T, U>(this: Set<T>, mapper: (value: T) => U): Set<U> {
    const list: U[] = [];
    this.forEach((value) => {
      list.push(mapper(value));
    });
    return new Set(list);
  },

  /**
   * wie Array.reduce
   * ruft reducer(acc,value) für jeden Eintrauf auf und gibt acc zurück
   * @param {function} reducer
   * @returns {any}
   */
  reduce<T>(this: Set<T>, reducer: any): any {
    return this.toArray().reduce(reducer);
  },

  /**
   * ein effizientes .map(mapper).find(x=>x)
   * @param {function} mapper
   * @returns {any}
   */
  findMap<T, U>(this: Set<T>, mapper: (v: T) => U | null | undefined): U | undefined {
    for (let v of this.values()) {
      const result = mapper(v);
      if (result != null) {
        return result;
      }
    }
    return undefined;
  },

  /**
   * wandelt den Set in ein Array um
   * @returns {Array}
   */
  toArray<T>(this: Set<T>): T[] {
    return Array.from(this);
  },
});

/**
 * Welcher Vollpfosten hatte diese glorreiche Idee: array.length aber set.size???????
 * Sowas ist einfach nur kognitiver Müll!
 * Das hier ist zwar nicht so performant, aber solche Fehler sind teilweise schwer zu finden,
 * daher lieber Bugfreiheit.
 * for the record: https://stackoverflow.com/questions/35038750/why-do-javascript-sets-maps-use-size-instead-of-length
 * Idioten!
 */
if (!Object.getOwnPropertyDescriptor(Set.prototype, "length")) {
  Object.defineProperty(Set.prototype, "length", {
    get() {
      return this.size;
    },
  });
}
